import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Modal, Radio, Space, Typography, message } from 'antd';
import { AdminNavBar } from '../components/AdminNavBar';
import { trackFeatureUsage } from '../lib/posthog';
import { ensurePicqerKeyPool, getWarehouses } from '../services/picqerAnalyseService';
import { ALL_MONTH_KEYS, computeUsedLocationPercent } from './PicqerAnalyse/computeUsedLocationPercent';
import {
  buildExportPayload,
  countExportStats,
  downloadJsonExport,
  downloadMetricsCsv,
  readImportFile,
} from './PicqerAnalyse/dataExport';
import { fetchAllLocationHistory, fetchAllLocations, getHistoryFetchConcurrency } from './PicqerAnalyse/dataLoader';
import {
  countExcludedParentLocations,
  filterAnalysableLocationsByWarehouse,
} from './PicqerAnalyse/locationLeafFilter';
import type {
  MetricMode,
  PicqerLocation,
  PicqerLocationStockHistoryEntry,
  PicqerWarehouse,
} from './PicqerAnalyse/types';
import { ANALYSE_SINCE_DATE, ANALYSE_UNTIL_DATE, PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY } from './PicqerAnalyse/types';
import { UsedLocationsChart } from './PicqerAnalyse/UsedLocationsChart';
import {
  DataPersistenceActions,
  FetchActions,
  type FetchPhase,
  WarehousePanel,
} from './PicqerAnalyse/WarehousePanel';

const { Title, Paragraph, Text } = Typography;

function resetFetchState() {
  return {
    warehouses: [] as PicqerWarehouse[],
    locationsByWarehouse: {} as Record<number, PicqerLocation[]>,
    historyByLocation: {} as Record<number, PicqerLocationStockHistoryEntry[]>,
    preRangeStockByLocation: {} as Record<number, number>,
    locationProgress: {} as Record<number, { loaded: number; page: number }>,
    historyProgress: { completed: 0, total: 0, currentLocationId: null as number | null },
    importStats: null as string | null,
  };
}

const PicqerAnalyseAdmin: React.FC = () => {
  const [warehouses, setWarehouses] = useState<PicqerWarehouse[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [locationsByWarehouse, setLocationsByWarehouse] = useState<Record<number, PicqerLocation[]>>({});
  const [historyByLocation, setHistoryByLocation] = useState<Record<number, PicqerLocationStockHistoryEntry[]>>({});
  const [preRangeStockByLocation, setPreRangeStockByLocation] = useState<Record<number, number>>({});
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [locationProgress, setLocationProgress] = useState<Record<number, { loaded: number; page: number }>>({});
  const [historyProgress, setHistoryProgress] = useState({ completed: 0, total: 0, currentLocationId: null as number | null });
  const [metricMode, setMetricMode] = useState<MetricMode>('any-time-in-month');
  const [monthFrom, setMonthFrom] = useState(ALL_MONTH_KEYS[0]);
  const [monthTo, setMonthTo] = useState(ALL_MONTH_KEYS[ALL_MONTH_KEYS.length - 1]);
  const [importStats, setImportStats] = useState<string | null>(null);
  const [fetchPhase, setFetchPhase] = useState<FetchPhase>('idle');
  const [fetchStatusText, setFetchStatusText] = useState<string | null>(null);
  const [apiKeyPoolSize, setApiKeyPoolSize] = useState(1);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    trackFeatureUsage('picqer_analyse_admin');
    ensurePicqerKeyPool().then((size) => setApiKeyPoolSize(size));
  }, []);

  const analysableLocationsByWarehouse = useMemo(
    () =>
      filterAnalysableLocationsByWarehouse(
        locationsByWarehouse,
        historyByLocation,
        preRangeStockByLocation,
      ),
    [locationsByWarehouse, historyByLocation, preRangeStockByLocation],
  );

  const allLocations = useMemo(
    () => Object.values(locationsByWarehouse).flat(),
    [locationsByWarehouse],
  );

  const analysableLocations = useMemo(
    () => Object.values(analysableLocationsByWarehouse).flat(),
    [analysableLocationsByWarehouse],
  );

  const totalLocationCount = analysableLocations.length;
  const totalRawLocationCount = allLocations.length;
  const historyLoadedCount = analysableLocations.filter(
    (l) => historyByLocation[l.idlocation] !== undefined,
  ).length;
  const hasData = warehouses.length > 0 || totalLocationCount > 0 || historyLoadedCount > 0;
  const isFetching = fetchPhase !== 'idle' && fetchPhase !== 'done';

  const clearAllData = () => {
    const reset = resetFetchState();
    setWarehouses(reset.warehouses);
    setLocationsByWarehouse(reset.locationsByWarehouse);
    setHistoryByLocation(reset.historyByLocation);
    setPreRangeStockByLocation(reset.preRangeStockByLocation);
    setLocationProgress(reset.locationProgress);
    setHistoryProgress(reset.historyProgress);
    setImportStats(reset.importStats);
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const runFetchWarehouses = async (): Promise<PicqerWarehouse[]> => {
    setWarehousesLoading(true);
    setFetchPhase('warehouses');
    setFetchStatusText('Fetching warehouses from Picqer…');
    try {
      const result = await getWarehouses();
      if (!result.success || result.data === undefined) {
        throw new Error(result.error ?? 'Failed to load warehouses');
      }
      if (result.data.length === 0) {
        throw new Error('Picqer returned 0 warehouses — check API key permissions');
      }
      setWarehouses(result.data);
      setFetchStatusText(`Loaded ${result.data.length} warehouses`);
      return result.data;
    } finally {
      setWarehousesLoading(false);
    }
  };

  const runFetchLocations = async (whs: PicqerWarehouse[]): Promise<Record<number, PicqerLocation[]>> => {
    setLocationsLoading(true);
    setFetchPhase('locations');
    setLocationProgress({});
    setFetchStatusText('Loading locations (paginated)…');
    try {
      const ids = whs.map((w) => w.idwarehouse);
      const loaded = await fetchAllLocations(ids, (idwarehouse, loadedCount, page) => {
        setLocationProgress((prev) => ({ ...prev, [idwarehouse]: { loaded: loadedCount, page } }));
        setFetchStatusText(`Loading locations… ${loadedCount} loaded (page ${page})`);
      }, abortRef.current?.signal);
      const total = Object.values(loaded).reduce((sum, locs) => sum + locs.length, 0);
      setLocationsByWarehouse(loaded);
      setFetchStatusText(`Loaded ${total} locations across ${whs.length} warehouses`);
      return loaded;
    } finally {
      setLocationsLoading(false);
    }
  };

  const runFetchHistory = async (
    locations: PicqerLocation[],
    forceRefresh: boolean,
  ): Promise<void> => {
    if (locations.length === 0) {
      throw new Error('No locations to fetch history for');
    }
    const poolSize = await ensurePicqerKeyPool();
    setApiKeyPoolSize(poolSize);
    const concurrency = getHistoryFetchConcurrency();
    setHistoryLoading(true);
    setFetchPhase('history');
    setFetchStatusText(
      `Loading stock history… 0 / ${locations.length} locations (${concurrency} workers, ${poolSize} API keys × ${PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY}/min)`,
    );
    try {
      await fetchAllLocationHistory(
        locations,
        forceRefresh ? {} : historyByLocation,
        (progress) => {
          setHistoryProgress(progress);
          setFetchStatusText(
            `Loading stock history… ${progress.completed} / ${progress.total} locations` +
              (progress.currentLocationId != null ? ` (#${progress.currentLocationId})` : ''),
          );
        },
        (idlocation, history, preRangeStock) => {
          setHistoryByLocation((prev) => ({ ...prev, [idlocation]: history }));
          setPreRangeStockByLocation((prev) => ({ ...prev, [idlocation]: preRangeStock }));
        },
        abortRef.current?.signal,
        { forceRefresh },
      );
      setFetchStatusText(`Stock history loaded for ${locations.length} locations`);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFreshFetch = () => {
    Modal.confirm({
      title: 'Fresh fetch from Picqer?',
      content:
        'This clears all loaded data and re-fetches warehouses, locations, and stock history from Picqer. This can take a long time.',
      okText: 'Start fresh fetch',
      onOk: async () => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        clearAllData();
        setFetchPhase('warehouses');
        try {
          const whs = await runFetchWarehouses();
          const loaded = await runFetchLocations(whs);
          const locations = Object.values(loaded).flat();
          await runFetchHistory(locations, true);
          setFetchPhase('done');
          message.success(`Fresh fetch complete — history loaded for ${locations.length} locations`);
        } catch (err) {
          setFetchPhase('idle');
          setFetchStatusText(null);
          if (err instanceof DOMException && err.name === 'AbortError') {
            message.info('Fetch cancelled');
          } else {
            message.error(err instanceof Error ? err.message : 'Fresh fetch failed');
          }
        } finally {
          abortRef.current = null;
        }
      },
    });
  };

  const handleFetchWarehouses = async () => {
    abortRef.current = new AbortController();
    try {
      const whs = await runFetchWarehouses();
      message.success(`Loaded ${whs.length} warehouses`);
      setFetchPhase('idle');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to load warehouses');
      setFetchPhase('idle');
      setFetchStatusText(null);
    } finally {
      abortRef.current = null;
    }
  };

  const handleLoadLocations = async () => {
    if (warehouses.length === 0) return;
    abortRef.current = new AbortController();
    try {
      const loaded = await runFetchLocations(warehouses);
      const total = Object.values(loaded).reduce((sum, locs) => sum + locs.length, 0);
      message.success(`Loaded ${total} locations — Export JSON now, then run step 3 when ready`);
      setFetchPhase('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        message.info('Cancelled');
      } else {
        message.error(err instanceof Error ? err.message : 'Failed to load locations');
      }
      setFetchPhase('idle');
      setFetchStatusText(null);
    } finally {
      abortRef.current = null;
    }
  };

  const handleLoadHistory = async () => {
    if (allLocations.length === 0) return;
    abortRef.current = new AbortController();
    try {
      await runFetchHistory(allLocations, false);
      message.success('Stock history loaded');
      setFetchPhase('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        message.info('History loading cancelled');
      } else {
        message.error(err instanceof Error ? err.message : 'Failed to load stock history');
      }
      setFetchPhase('idle');
      setFetchStatusText(null);
    } finally {
      abortRef.current = null;
    }
  };

  const handleClearData = () => {
    handleCancel();
    clearAllData();
    setFetchPhase('idle');
    setFetchStatusText(null);
    message.info('Loaded data cleared');
  };

  const handleImport = async (file: File) => {
    setImportLoading(true);
    try {
      const payload = await readImportFile(file);
      setWarehouses(payload.warehouses);
      setLocationsByWarehouse(payload.locationsByWarehouse);
      setHistoryByLocation(payload.historyByLocation);
      setPreRangeStockByLocation(payload.preRangeStockByLocation);
      setFetchPhase('done');
      setFetchStatusText(null);
      const stats = countExportStats(payload);
      const stageHint =
        stats.historyLocationCount >= stats.locationCount && stats.locationCount > 0
          ? ' — ready for chart or re-export'
          : stats.locationCount > 0
            ? ' — import and run step 3 (stock history)'
            : stats.warehouseCount > 0
              ? ' — import and run step 2 (locations)'
              : '';
      setImportStats(
        `Imported ${stats.warehouseCount} warehouses, ${stats.locationCount} locations, ` +
          `${stats.historyLocationCount} with history (exported ${stats.exportedAt.slice(0, 10)})${stageHint}`,
      );
      message.success('Data imported');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to import file');
    } finally {
      setImportLoading(false);
    }
  };

  const { chartData, summaryRows, monthlyMetrics, monthKeys } = useMemo(() => {
    if (historyLoadedCount === 0) {
      return { chartData: [], summaryRows: [], monthlyMetrics: [], monthKeys: [] as string[] };
    }
    const filteredLocationsByWarehouse: Record<number, PicqerLocation[]> = {};
    for (const wh of warehouses) {
      filteredLocationsByWarehouse[wh.idwarehouse] = (
        analysableLocationsByWarehouse[wh.idwarehouse] ?? []
      ).filter((l) => historyByLocation[l.idlocation] !== undefined);
    }
    const result = computeUsedLocationPercent({
      warehouses,
      locationsByWarehouse: filteredLocationsByWarehouse,
      allLocationsByWarehouse: analysableLocationsByWarehouse,
      historyByLocation,
      preRangeStockByLocation,
      metricMode,
      monthFrom,
      monthTo,
    });
    return result;
  }, [
    warehouses,
    analysableLocationsByWarehouse,
    historyByLocation,
    preRangeStockByLocation,
    metricMode,
    monthFrom,
    monthTo,
    historyLoadedCount,
  ]);

  const handleExportJson = () => {
    const payload = buildExportPayload(
      warehouses,
      locationsByWarehouse,
      historyByLocation,
      preRangeStockByLocation,
    );
    downloadJsonExport(payload);
    const stage = payload.exportStage ?? 'snapshot';
    message.success(
      stage === 'locations'
        ? `Exported ${totalLocationCount} locations — import later to run step 3`
        : stage === 'complete'
          ? 'Full snapshot exported'
          : 'JSON exported',
    );
  };

  const handleExportCsv = () => {
    if (monthlyMetrics.length === 0) {
      message.warning('No metrics to export — load or import history first');
      return;
    }
    downloadMetricsCsv(monthlyMetrics);
    message.success('Metrics CSV exported');
  };

  return (
    <>
      <AdminNavBar />
      <div style={{ padding: '0 24px 48px', maxWidth: 1200, margin: '0 auto' }}>
        <Title level={2}>Picqer analyse</Title>
        <Paragraph type="secondary">
          Used location % per warehouse, {ANALYSE_SINCE_DATE.slice(0, 10)} to{' '}
          {ANALYSE_UNTIL_DATE.slice(0, 10)}. Use <strong>Fresh fetch</strong> for a new Picqer pull, or import a saved JSON.
        </Paragraph>

        <FetchActions
          onFreshFetch={handleFreshFetch}
          onFetchWarehouses={handleFetchWarehouses}
          onLoadLocations={handleLoadLocations}
          onLoadHistory={handleLoadHistory}
          onCancel={handleCancel}
          fetchPhase={fetchPhase}
          fetchStatusText={fetchStatusText}
          warehousesLoading={warehousesLoading}
          locationsLoading={locationsLoading}
          historyLoading={historyLoading}
          isFetching={isFetching}
          hasWarehouses={warehouses.length > 0}
          hasLocations={totalLocationCount > 0}
          apiKeyPoolSize={apiKeyPoolSize}
          historyConcurrency={getHistoryFetchConcurrency()}
        />

        <DataPersistenceActions
          onImport={handleImport}
          onExportJson={handleExportJson}
          onExportCsv={handleExportCsv}
          onClearData={handleClearData}
          importLoading={importLoading}
          canExportJson={warehouses.length > 0}
          canExportCsv={historyLoadedCount > 0}
          hasData={hasData}
          importStats={importStats}
        />

        {!hasData && !isFetching && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message='Click "Fresh fetch from Picqer" above, or import a previously exported JSON file.'
          />
        )}

        <Space style={{ marginBottom: 16 }}>
          <Text type="secondary">Metric:</Text>
          <Radio.Group
            value={metricMode}
            onChange={(e) => setMetricMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="any-time-in-month">Any time in month</Radio.Button>
            <Radio.Button value="avg-month-occupation">Avg % of month occupied</Radio.Button>
            <Radio.Button value="month-end-snapshot">Month-end snapshot</Radio.Button>
          </Radio.Group>
        </Space>

        {warehouses.length > 0 && (
          <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 24 }}>
            {warehouses.map((wh) => {
              const rawLocations = locationsByWarehouse[wh.idwarehouse] ?? [];
              const analysable = analysableLocationsByWarehouse[wh.idwarehouse] ?? [];
              return (
                <WarehousePanel
                  key={wh.idwarehouse}
                  warehouse={wh}
                  locations={analysable}
                  parentLocationCount={countExcludedParentLocations(
                    rawLocations,
                    historyByLocation,
                    preRangeStockByLocation,
                  )}
                  locationsLoading={locationsLoading}
                  locationProgress={locationProgress[wh.idwarehouse] ?? null}
                  historyLoaded={
                    analysable.filter((l) => historyByLocation[l.idlocation] !== undefined).length
                  }
                  historyTotal={analysable.length}
                  historyFetchTotal={rawLocations.length}
                  historyFetchLoaded={
                    rawLocations.filter((l) => historyByLocation[l.idlocation] !== undefined).length
                  }
                  historyLoading={historyLoading}
                  currentLocationId={historyProgress.currentLocationId}
                />
              );
            })}
          </Space>
        )}

        {chartData.length > 0 && (
          <Card title="Monthly location occupation">
            <UsedLocationsChart
              warehouses={warehouses}
              chartData={chartData}
              summaryRows={summaryRows}
              monthlyMetrics={monthlyMetrics}
              metricMode={metricMode}
              monthFrom={monthFrom}
              monthTo={monthTo}
              onMonthRangeChange={(from, to) => {
                setMonthFrom(from);
                setMonthTo(to);
              }}
              locationsByWarehouse={analysableLocationsByWarehouse}
              historyByLocation={historyByLocation}
              preRangeStockByLocation={preRangeStockByLocation}
              monthKeys={monthKeys}
            />
          </Card>
        )}

        {historyLoadedCount > 0 && historyLoadedCount < totalLocationCount && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
            message={`Partial data: ${historyLoadedCount} of ${totalLocationCount} analysable locations loaded (history fetched for ${Object.values(historyByLocation).length} of ${totalRawLocationCount} total). Chart reflects loaded locations only.`}
          />
        )}
      </div>
    </>
  );
};

export default PicqerAnalyseAdmin;
